/**
 * Every tunable number in the simulation lives here.
 *
 * Values are the provisional starting points from the game brief (§5). The four
 * flagged as VOLATILE are the ones most likely to move after the first external
 * playtest — do not author a room whose solution depends on their exact value.
 */

// --- Playfield (brief §5) ---
export const GRID_W = 16;
export const GRID_H = 12;

// --- Timestep. Fixed, never variable. Determinism is pillar 2. ---
export const TICK_HZ = 60;
export const DT = 1 / TICK_HZ;

// --- Player body ---
export const PLAYER_HALF_W = 0.35;
export const PLAYER_H = 0.9;

// --- Movement (brief §5) ---
export const WALK_SPEED = 2.4; // tiles/s (was 4.8; halved 2026-09-06, direct request)
/**
 * Fixed absolute values (tiles/s²), not derived from WALK_SPEED, as they were
 * before 2026-09-06's halving - kept at exactly what they evaluated to
 * beforehand (4.8/0.08 and 4.8/0.06). `updatePlayer` uses these same two
 * constants for airborne acceleration too, regardless of grounded state
 * (only the speed *cap* differs, WALK_SPEED vs AIR_SPEED); re-deriving them
 * from the new, lower WALK_SPEED would have halved how fast a standing jump
 * ramps up toward the AIR_SPEED cap, measurably breaking "a running jump
 * covers the same ground as a standing one" (`tests/movement.test.ts`) even
 * though AIR_SPEED itself was left untouched. Kept snappy on purpose: making
 * the character slower to reach its (now lower) top speed was never part of
 * the request, only the top speed itself.
 */
export const WALK_ACCEL = 60; // was WALK_SPEED / 0.08, i.e. 4.8 / 0.08
export const WALK_DECEL = 80; // was WALK_SPEED / 0.06, i.e. 4.8 / 0.06

/**
 * VOLATILE. The single most important number in the game.
 *
 * Revised 2026-09-04 from 2.25 to 1.25 tiles. The rule it has to satisfy is
 * unchanged in spirit but tighter in practice: a jump must clear exactly ONE
 * tile - the height of a single cast block - and fail to clear two. That is
 * what forces the player to build a staircase one step at a time instead of
 * skipping steps.
 *
 * Rise time drops with it so the arc stays snappy rather than floaty; a lower
 * apex over the old 0.32s would hang in the air.
 *
 * Changing either number invalidates every recorded room solution. The
 * solvability harness will fail rather than let that ship silently.
 */
export const JUMP_APEX = 1.25;
export const JUMP_RISE_TIME = 0.26;
export const JUMP_VELOCITY = (2 * JUMP_APEX) / JUMP_RISE_TIME;
export const GRAVITY = JUMP_VELOCITY / JUMP_RISE_TIME;
export const MAX_FALL_SPEED = 22;

/**
 * Horizontal speed cap while airborne, tiles/s.
 *
 * Added 2026-09-04 as a fraction of WALK_SPEED (0.6x); deliberately decoupled
 * into its own fixed value on 2026-09-06, when WALK_SPEED was halved for
 * ground movement alone. Airtime is fixed by the jump arc (JUMP_VELOCITY /
 * GRAVITY, untouched), so jump *reach* in tiles is directly proportional to
 * this number alone - halving it alongside WALK_SPEED would have halved
 * every jump's horizontal reach too, from ~1.5 tiles to ~0.75, which is no
 * longer enough to clear even the 1-tile gap every staircase in every room
 * is built around. That is a room-geometry break, not a re-timing one, so
 * this stays exactly what WALK_SPEED × 0.6 used to evaluate to before the
 * halving: a jump still spans ~1.5 tiles, unchanged.
 *
 * It is a hard clamp rather than drag. This is a puzzle-platformer, so a jump
 * that always covers the same distance is worth more than one that preserves
 * run-up momentum.
 */
export const AIR_SPEED = 2.88;

// Fairness affordances. These make no puzzle easier, only inputs less cruel.
export const COYOTE_TIME = 0.08;
export const JUMP_BUFFER = 0.1;

// --- Cast, the core verb (brief §5) ---
export const CAST_COOLDOWN = 0.14; // VOLATILE
export const CAST_SPAWN_TIME = 0.1; // block is NOT solid during this window
export const CRUMBLE_TIME = 0.12; // block is NOT solid during this window

/**
 * Head-butts needed to break a castable block from below.
 *
 * Two rather than one: a single hit would make every accidental jump under a
 * staircase demolish it, and the staircase is the thing the player just spent
 * effort building. Two is deliberate without being laborious.
 */
export const BLOCK_HITS_TO_BREAK = 2;

// --- Moonlight: the timer is the life bar ---
export const MOONLIGHT_START = 90; // VOLATILE
export const MOONSHARD_VALUE = 10; // VOLATILE

// --- Chimera (brief §6). All deterministic, no RNG anywhere. ---
export const CREEP_SPEED = 1.0; // tiles/s (was 2.0; halved 2026-09-06, direct request)
// Seconds per one-tile step (was 0.5; doubled 2026-09-06 - halves the
// Drifter's effective speed the same way halving a continuous speed would).
export const DRIFTER_STEP_INTERVAL = 1.0;
export const SOWER_INTERVAL = 6.0; // unaffected: a Sower never moves itself

/**
 * The Warden (brief §5). Reuses the Drifter's step-toward-player movement, so
 * it needs no constants of its own beyond what marks it unroutable - that is
 * a code path (`allowVertical` in `updateDrifter`), not a number, and lives
 * in sim.ts.
 */

// --- The ranged skill (Tail). Kills what a cast block never can. ---
export const RANGED_RANGE = 6; // tiles, along the feet row, blocked by any solid tile
export const RANGED_COOLDOWN = 0.25; // seconds between shots

// --- Numerical epsilon for tile/edge tests ---
export const EPS = 1e-6;
